<template>
  <div class="currencies-container">
    <!-- Header -->
    <div class="currencies-header">
      <h3 class="section-title">Platform Currencies</h3>
      <p class="section-desc">Manage the currencies available for payments and pricing across the platform.</p>
    </div>

    <!-- Currency List -->
    <div class="currencies-card">
      <div class="currencies-list">
        <transition-group name="list" tag="div" class="currency-items">
          <div
            v-for="(currency, index) in currencies"
            :key="currency"
            class="currency-item"
          >
            <div class="currency-info">
              <span class="currency-code">{{ currency }}</span>
              <span class="currency-name">{{ getCurrencyName(currency) }}</span>
            </div>
            <button class="btn-remove" @click="removeCurrency(index)" title="Remove currency">
              &times;
            </button>
          </div>
        </transition-group>

        <!-- Empty state -->
        <div v-if="currencies.length === 0" class="empty-state">
          <p>No currencies configured</p>
        </div>
      </div>

      <!-- Add Currency -->
      <div class="add-currency-section">
        <div class="add-currency-row">
          <select v-model="newCurrency" class="currency-select">
            <option value="">Select currency to add...</option>
            <option
              v-for="curr in availableCurrencies"
              :key="curr.code"
              :value="curr.code"
            >{{ curr.code }} - {{ curr.name }}</option>
          </select>
          <button
            class="btn-add"
            :disabled="!newCurrency"
            @click="addCurrency"
          >Add Currency</button>
        </div>
      </div>

      <!-- Status -->
      <transition name="fade">
        <div v-if="saving" class="status saving">Saving...</div>
        <div v-else-if="saved" class="status saved">Saved</div>
      </transition>
    </div>
  </div>
</template>

<script>
// Common currencies list
const CURRENCY_LIST = [
  { code: 'USD', name: 'US Dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'SEK', name: 'Swedish Krona' },
  { code: 'NOK', name: 'Norwegian Krone' },
  { code: 'DKK', name: 'Danish Krone' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'BRL', name: 'Brazilian Real' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'KRW', name: 'South Korean Won' },
  { code: 'PLN', name: 'Polish Zloty' },
  { code: 'ZAR', name: 'South African Rand' },
  { code: 'HKD', name: 'Hong Kong Dollar' },
  { code: 'SGD', name: 'Singapore Dollar' }
]

export default {
  data() {
    return {
      currencies: [],
      newCurrency: '',
      saving: false,
      saved: false,
      saveTimeout: null
    }
  },

  computed: {
    availableCurrencies() {
      return CURRENCY_LIST.filter(c => !this.currencies.includes(c.code))
    }
  },

  methods: {
    getCurrencyName(code) {
      const found = CURRENCY_LIST.find(c => c.code === code)
      return found ? found.name : code
    },

    load() {
      fetch('/admin/api/v2/config/currencies')
        .then(resp => {
          if (resp.status === 200) return resp.json()
          if (resp.status === 404) return null
          throw Error(resp.statusText)
        })
        .then(resp => {
          if (resp && resp.data && Array.isArray(resp.data.value)) {
            this.currencies = resp.data.value
          }
        })
        .catch(() => {
          // Use empty array if config doesn't exist
        })
    },

    save() {
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout)
      }

      this.saving = true
      this.saved = false

      fetch('/admin/api/v2/config/currencies', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            value: this.currencies,
            is_public: true
          }
        })
      })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.saving = false
          this.saved = true
          this.saveTimeout = setTimeout(() => {
            this.saved = false
          }, 2000)
        })
        .catch(() => {
          this.saving = false
          this.$root.app.showAlert('Error saving currencies', 'danger')
        })
    },

    addCurrency() {
      if (this.newCurrency && !this.currencies.includes(this.newCurrency)) {
        this.currencies.push(this.newCurrency)
        this.newCurrency = ''
        this.save()
      }
    },

    removeCurrency(index) {
      this.currencies.splice(index, 1)
      this.save()
    }
  },

  mounted() {
    this.$root.app.setActivePageTitle('Currencies', false)
    this.$root.$emit('set-toolbar', 'empty-toolbar')
    this.load()
  }
}
</script>


<style scoped>
.currencies-container {
  padding: 1.5rem;
  max-width: 600px;
  margin: 0 auto;
}

.currencies-header {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.section-desc {
  color: #94A3B8;
  font-size: 0.9rem;
  margin: 0;
}

.currencies-card {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  padding: 1.25rem;
  position: relative;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.currencies-list {
  margin-bottom: 1rem;
}

.currency-items {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.currency-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.2s;
}

.currency-item:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
}

.currency-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.currency-code {
  font-weight: 600;
  color: #fff;
  font-size: 1rem;
  min-width: 45px;
}

.currency-name {
  color: #94A3B8;
  font-size: 0.85rem;
}

.btn-remove {
  background: transparent;
  border: none;
  color: #64748B;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  line-height: 1;
  border-radius: 4px;
  transition: all 0.15s;
}

.btn-remove:hover {
  background: rgba(220, 53, 69, 0.1);
  color: #ef4444;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #94A3B8;
  font-style: italic;
}

.add-currency-section {
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.add-currency-row {
  display: flex;
  gap: 0.75rem;
}

.currency-select {
  flex: 1;
  padding: 0.6rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 0.9rem;
  cursor: pointer;
  transition: border-color 0.2s;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%2394A3B8%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
  background-repeat: no-repeat;
  background-position: right 0.7rem top 50%;
  background-size: 0.65rem auto;
}

.currency-select:focus {
  outline: none;
  border-color: #20B4FF;
  background-color: rgba(0, 0, 0, 0.5);
}

.currency-select option {
  background: #1e2227;
  color: #fff;
  padding: 10px;
}

.btn-add {
  padding: 0.6rem 1.25rem;
  background: linear-gradient(135deg, #20B4FF 0%, #007BFF 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.2s;
  box-shadow: 0 4px 6px rgba(32, 180, 255, 0.2);
}

.btn-add:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 12px rgba(32, 180, 255, 0.3);
}

.btn-add:disabled {
  background: rgba(255, 255, 255, 0.05);
  color: #64748B;
  cursor: not-allowed;
  box-shadow: none;
}

.status {
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.status.saving {
  color: #94A3B8;
}

.status.saved {
  color: #28a745;
  background: rgba(40, 167, 69, 0.15);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.list-enter-active,
.list-leave-active {
  transition: all 0.2s ease;
}

.list-enter,
.list-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

.list-move {
  transition: transform 0.2s ease;
}
</style>
